import {
  DateValue,
  getWeekNumber,
  isSameDay,
  plusDays,
  toDate,
  toFirstOfMonth,
  toFirstOfWeek,
  toLastOfMonth,
  toLastOfWeek,
} from "@/utils/Dates";
import { chunk } from "lodash";

export type CalendarDay<CalendarEvent = unknown> = {
  date: DateValue;
  day: number;
  isToday?: boolean;
  isSelected?: boolean;
  isWeekend?: boolean;
  isOutsideMonth?: boolean;
  events?: CalendarEvent[];
};

export type CalendarWeek<CalendarEvent = unknown> = {
  days: CalendarDay<CalendarEvent>[];
  week: number;
};

export type Calendar<CalendarEvent = unknown> = {
  month: DateValue;
  weeks: CalendarWeek<CalendarEvent>[];
};

export type CalendarParams<CalendarEvent = unknown> =
  | {
      month: DateValue;
      today: DateValue;
      selected?: DateValue | null;
      events?: undefined;
      eventDate?: undefined;
    }
  | {
      month: DateValue;
      today: DateValue;
      selected?: DateValue | null;
      events: CalendarEvent[];
      eventDate: (e: CalendarEvent) => DateValue;
    };

export const makeCalendar = <CalendarEvent>(
  params: CalendarParams<CalendarEvent>,
): Calendar<CalendarEvent> => {
  const firstOfMonth = toFirstOfMonth(params.month);
  const firstOfWeek = toFirstOfWeek(firstOfMonth);
  const lastOfMonth = toLastOfMonth(params.month);
  const lastOfWeek = toLastOfWeek(lastOfMonth);
  const days: CalendarDay<CalendarEvent>[] = [];

  for (let date = firstOfWeek; date.getTime() <= lastOfWeek.getTime(); date = plusDays(date, 1)) {
    days.push({
      date,
      day: toDate(date).getDate(),
      isOutsideMonth: date.getMonth() !== firstOfMonth.getMonth(),
      isToday: isSameDay(date, params.today),
      isSelected: params.selected ? isSameDay(date, params.selected) : undefined,
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      events: params.events
        ? params.events.filter((e) => isSameDay(params.eventDate?.(e), date))
        : undefined,
    });
  }

  return {
    month: firstOfMonth,
    weeks: chunk(days, 7).map((days) => ({
      days: days,
      week: getWeekNumber(toDate(days[0].date)),
    })),
  };
};
